import { useState, useEffect, useCallback } from 'react';
import { Scale, Droplets, Filter, ThermometerSun, Check, RotateCw, Beaker, ListChecks, FlaskConical } from 'lucide-react';
import InfoBox from '../InfoBox';
import { FilterApparatus, AnalyticalBalance } from '@/components/lab';

const steps = [
  { id: 1, title: 'Weigh Fertilizer', short: 'Weigh ~1.0 g on analytical balance' },
  { id: 2, title: 'Dissolve in Water', short: 'Add 25–30 mL H₂O' },
  { id: 3, title: 'Filter Insolubles', short: 'Remove undissolved material' },
  { id: 4, title: 'Add MgSO₄', short: 'Add excess MgSO₄ solution' },
  { id: 5, title: 'Add Ammonia', short: 'Add NH₃ until pH > 9' },
  { id: 6, title: 'Stir to Crystallize', short: 'Gentle stirring for crystal growth' },
  { id: 7, title: 'Filter Precipitate', short: 'Vacuum filtration' },
  { id: 8, title: 'Wash Precipitate', short: 'H₂O then isopropyl alcohol' },
  { id: 9, title: 'Dry Precipitate', short: 'Dry at ~100°C to constant mass' },
  { id: 10, title: 'Calculate %P & %P₂O₅', short: 'Use gravimetric factors' },
];

// Educational content for each step
const stepInfoContent: Record<number, { title: string; content: string; whatHappens: string; observe: string[] }> = {
  1: {
    title: 'Weighing Fertilizer Sample',
    content: 'Fertilizer samples are analyzed for phosphorus content, which is typically reported as %P or %P₂O₅. Accurate weighing of approximately 1.0g sample ensures we have enough material for reliable precipitation while keeping reagent volumes manageable.',
    whatHappens: 'The fertilizer sample is weighed on an analytical balance with 0.0001g precision.',
    observe: [
      'Fertilizer may be granular or powdered',
      'Weigh ~1.0g accurately',
      'Record mass to 4 decimal places',
      'Sample should be representative'
    ]
  },
  2: {
    title: 'Dissolving in Water',
    content: 'Soluble phosphate fertilizers (like superphosphate) contain water-soluble phosphate compounds. Dissolving in water extracts the phosphate ions that will be analyzed. Some insoluble materials may remain.',
    whatHappens: 'Water is added and the soluble phosphate dissolves. Insoluble materials (sand, fillers) may remain.',
    observe: [
      'Stir to aid dissolution',
      'Some solid may remain (fillers)',
      'Phosphate goes into solution',
      'Solution may be colored'
    ]
  },
  3: {
    title: 'Filtering Insolubles',
    content: 'Filtering removes insoluble materials (sand, calcium sulfate, etc.) that are present in many fertilizers. Only the soluble phosphate in the filtrate will be analyzed.',
    whatHappens: 'The mixture is filtered to remove undissolved material. The filtrate contains dissolved phosphate.',
    observe: [
      'Rinse residue with water',
      'Collect all filtrate',
      'Filtrate should be clear',
      'Discard the solid residue'
    ]
  },
  4: {
    title: 'Adding Magnesium Sulfate',
    content: 'MgSO₄ provides Mg²⁺ ions needed for the precipitation. The reaction also requires ammonium ions (from NH₄OH in the next step):\n\nMg²⁺ + NH₄⁺ + PO₄³⁻ + 6H₂O → MgNH₄PO₄·6H₂O',
    whatHappens: 'Excess MgSO₄ solution is added to provide magnesium ions.',
    observe: [
      'Add excess MgSO₄',
      'Solution remains clear',
      'Mg²⁺ now in solution',
      'Ready for precipitation'
    ]
  },
  5: {
    title: 'Adding Ammonia (pH > 9)',
    content: 'Ammonia serves two purposes:\n1. Provides NH₄⁺ ions for the precipitate\n2. Makes the solution basic (pH > 9) so PO₄³⁻ predominates\n\nThe reaction requires basic conditions for complete precipitation of MgNH₄PO₄·6H₂O (struvite).',
    whatHappens: 'Ammonia is added slowly until pH exceeds 9. White precipitate begins to form.',
    observe: [
      'Add ammonia slowly',
      'White precipitate forms',
      'Check pH with indicator',
      'Continue until pH > 9'
    ]
  },
  6: {
    title: 'Stirring to Crystallize',
    content: 'Slow stirring allows the MgNH₄PO₄·6H₂O precipitate to form well-defined crystals. Rapid addition of ammonia produces fine particles that are difficult to filter. Stirring promotes crystal growth and settling.',
    whatHappens: 'Continued gentle stirring allows the precipitate to form larger, more filterable crystals.',
    observe: [
      'Stir gently and continuously',
      'Crystals grow larger',
      'Precipitate becomes more compact',
      'Allow to settle before filtering'
    ]
  },
  7: {
    title: 'Filtering the Precipitate',
    content: 'The MgNH₄PO₄·6H₂O precipitate is collected by vacuum filtration. This compound is the gravimetric form from which phosphorus content is calculated.',
    whatHappens: 'The precipitate is filtered and collected. Vacuum filtration speeds the process.',
    observe: [
      'White crystalline precipitate',
      'Transfer all precipitate',
      'Rinse beaker into filter',
      'Vacuum aids drainage'
    ]
  },
  8: {
    title: 'Washing the Precipitate',
    content: 'First wash with two or three 5 mL portions of deionised water, adding each portion to the beaker in which you did the precipitation to transfer any remaining precipitate, then pour over the solid on the funnel. Finally, pour two 10 mL portions of 75% isopropyl alcohol through the filter paper to displace water and speed drying. CAUTION: Isopropyl alcohol is flammable!',
    whatHappens: 'The precipitate is washed with deionised water then 75% isopropyl alcohol.',
    observe: [
      'Wash with 2-3 portions of 5 mL deionised water',
      'Then 2 portions of 10 mL 75% isopropyl alcohol',
      'Precipitate remains white',
      'Isopropyl alcohol is flammable - no heat sources'
    ]
  },
  9: {
    title: 'Drying the Precipitate',
    content: 'MgNH₄PO₄·6H₂O is dried at ~100°C in an oven on a numbered watch glass. When dry, weigh the filter paper/precipitate and calculate the mass.',
    whatHappens: 'The precipitate is dried at ~100°C to constant mass.',
    observe: [
      'Dry at ~100°C in oven',
      'Place on numbered watch glass',
      'Cool in desiccator',
      'Weigh when cool'
    ]
  },
  10: {
    title: 'Calculating %P and %P₂O₅',
    content: 'Two gravimetric factors are used:\n\nGF(P) = MW(P) / MW(MgNH₄PO₄·6H₂O) = 30.97/245.40 = 0.1262\nGF(P₂O₅) = MW(P₂O₅)/(2 × MW precipitate) = 141.92/490.80 = 0.2892\n\nFertilizers are often labeled as %P₂O₅ for historical reasons.',
    whatHappens: 'The mass of precipitate is used to calculate %P and %P₂O₅.',
    observe: [
      'Calculate using gravimetric factors',
      '%P = (mass × 0.1262 / sample mass) × 100',
      '%P₂O₅ = (mass × 0.2891 / sample mass) × 100',
      'Compare to fertilizer label'
    ]
  }
};

interface Part2PhosphorusProps {
  onStepComplete?: (stepId: string) => void;
  currentStep?: number;
}

export default function Part2Phosphorus({ onStepComplete, currentStep = 1 }: Part2PhosphorusProps) {
  const [showInfo, setShowInfo] = useState<number | null>(null);

  // State management
  const [balanceTared, setBalanceTared] = useState(false);
  const [fertilizerMass, setFertilizerMass] = useState<number | null>(null);
  const [dissolved, setDissolved] = useState(false);
  const [insolubesFiltered, setInsolublesFiltered] = useState(false);
  const [mgso4Added, setMgso4Added] = useState(false);
  const [ammoniaAdded, setAmmoniaAdded] = useState(false);
  const [ammoniaProgress, setAmmoniaProgress] = useState(0);
  const [precipitateAmount, setPrecipitateAmount] = useState(0);
  const [stirring, setStirring] = useState(false);
  const [crystalline, setCrystalline] = useState(false);
  const [vacuumOn, setVacuumOn] = useState(false);
  const [filterProgress, setFilterProgress] = useState(0);
  const [washed, setWashed] = useState(false);
  const [dryingProgress, setDryingProgress] = useState(0);
  const [dryingComplete, setDryingComplete] = useState(false);
  const [precipitateMass, setPrecipitateMass] = useState<number | null>(null);
  const [secondMass, setSecondMass] = useState<number | null>(null);
  const [percentP, setPercentP] = useState<number | null>(null);
  const [percentP2O5, setPercentP2O5] = useState<number | null>(null);

  // Ammonia addition simulation
  useEffect(() => {
    if (ammoniaProgress > 0 && ammoniaProgress < 100) {
      const interval = setInterval(() => {
        setAmmoniaProgress(prev => Math.min(prev + 1.5, 100));
        if (ammoniaProgress > 30) {
          setPrecipitateAmount(prev => Math.min(prev + 2, 100));
        }
      }, 100);
      return () => clearInterval(interval);
    }
    if (ammoniaProgress >= 100 && !ammoniaAdded) {
      setAmmoniaAdded(true);
      if (onStepComplete) onStepComplete('phosphorus-5');
    }
  }, [ammoniaProgress, ammoniaAdded, onStepComplete]);

  // Stirring/crystallization simulation
  useEffect(() => {
    if (stirring && ammoniaAdded && !crystalline) {
      const timeout = setTimeout(() => {
        setCrystalline(true);
        setStirring(false);
        if (onStepComplete) onStepComplete('phosphorus-6');
      }, 3000);
      return () => clearTimeout(timeout);
    }
  }, [stirring, ammoniaAdded, crystalline, onStepComplete]);

  // Filtration simulation
  useEffect(() => {
    if (vacuumOn && filterProgress < 100) {
      const interval = setInterval(() => {
        setFilterProgress(prev => Math.min(prev + 1.5, 100));
      }, 100);
      return () => clearInterval(interval);
    }
  }, [vacuumOn, filterProgress]);

  // Drying simulation
  useEffect(() => {
    if (washed && dryingProgress > 0 && dryingProgress < 100) {
      const interval = setInterval(() => {
        setDryingProgress(prev => Math.min(prev + 1, 100));
      }, 100);
      return () => clearInterval(interval);
    }
    if (dryingProgress >= 100 && !dryingComplete) {
      setDryingComplete(true);
      if (onStepComplete) onStepComplete('phosphorus-9');
    }
  }, [washed, dryingProgress, dryingComplete, onStepComplete]);

  const handleTare = useCallback(() => {
    setBalanceTared(true);
  }, []);

  const handleWeighFertilizer = useCallback(() => {
    if (balanceTared) {
      const mass = 0.95 + Math.random() * 0.1; // 0.95-1.05g
      setFertilizerMass(parseFloat(mass.toFixed(4)));
      if (onStepComplete) onStepComplete('phosphorus-1');
    }
  }, [balanceTared, onStepComplete]);

  const handleDissolve = useCallback(() => {
    if (fertilizerMass) {
      setDissolved(true);
      if (onStepComplete) onStepComplete('phosphorus-2');
    }
  }, [fertilizerMass, onStepComplete]);

  const handleFilterInsolubles = useCallback(() => {
    if (dissolved) {
      setInsolublesFiltered(true);
      if (onStepComplete) onStepComplete('phosphorus-3');
    }
  }, [dissolved, onStepComplete]);

  const handleAddMgSO4 = useCallback(() => {
    if (insolubesFiltered) {
      setMgso4Added(true);
      if (onStepComplete) onStepComplete('phosphorus-4');
    }
  }, [insolubesFiltered, onStepComplete]);

  const handleAddAmmonia = useCallback(() => {
    if (mgso4Added) {
      setAmmoniaProgress(1);
    }
  }, [mgso4Added]);

  const handleStir = useCallback(() => {
    if (ammoniaAdded) {
      setStirring(true);
    }
  }, [ammoniaAdded]);

  const handleStartFiltration = useCallback(() => {
    if (crystalline) {
      setVacuumOn(true);
      if (onStepComplete) onStepComplete('phosphorus-7');
    }
  }, [crystalline, onStepComplete]);

  const handleWash = useCallback(() => {
    if (filterProgress >= 100) {
      setWashed(true);
      if (onStepComplete) onStepComplete('phosphorus-8');
    }
  }, [filterProgress, onStepComplete]);

  const handleStartDrying = useCallback(() => {
    if (washed) {
      setDryingProgress(1);
    }
  }, [washed]);

  const handleWeighPrecipitate = useCallback(() => {
    if (dryingComplete && !precipitateMass) {
      // MgNH4PO4.6H2O precipitate mass
      const mass = (fertilizerMass || 1) * 0.4 + Math.random() * 0.05;
      setPrecipitateMass(parseFloat(mass.toFixed(4)));
    }
  }, [dryingComplete, fertilizerMass, precipitateMass]);

  const handleSecondWeigh = useCallback(() => {
    if (precipitateMass && !secondMass) {
      const mass = precipitateMass + (Math.random() - 0.5) * 0.0004;
      setSecondMass(parseFloat(mass.toFixed(4)));
    }
  }, [precipitateMass, secondMass]);

  const handleCalculate = useCallback(() => {
    if (secondMass && fertilizerMass) {
      // Gravimetric factors
      const gfP = 0.1262; // P/MgNH4PO4.6H2O
      const gfP2O5 = 0.2891; // P2O5/(2*MgNH4PO4.6H2O)
      const avgMass = (precipitateMass! + secondMass) / 2;

      const pPercent = (avgMass * gfP / fertilizerMass) * 100;
      const p2o5Percent = (avgMass * gfP2O5 / fertilizerMass) * 100;

      setPercentP(parseFloat(pPercent.toFixed(2)));
      setPercentP2O5(parseFloat(p2o5Percent.toFixed(2)));
      if (onStepComplete) onStepComplete('phosphorus-10');
    }
  }, [secondMass, fertilizerMass, precipitateMass, onStepComplete]);

  // Get precipitate color based on crystallinity
  const getPrecipitateColor = () => {
    if (!ammoniaAdded) return '#F5F5F5';
    if (crystalline) return '#FFFFFF';
    return '#FFFAF0';
  };

  // Derive step completion from state
  const stepDone = [
    !!fertilizerMass, dissolved, insolubesFiltered, mgso4Added,
    ammoniaAdded, crystalline, filterProgress >= 100,
    washed, dryingComplete, !!percentP
  ];
  const completedCount = stepDone.filter(Boolean).length;
  const activeStep = stepDone.indexOf(false) + 1 || steps.length;

  return (
    <div className="exp7-part-layout">
      {/* Steps Panel */}
      <div className="exp7-steps-panel">
        <div className="exp7-steps-header">
          <ListChecks size={16} />
          <h3>Part II: Phosphorus</h3>
        </div>
        <div className="exp7-steps-list">
          {steps.map(s => {
            const done = stepDone[s.id - 1];
            const active = s.id === activeStep;
            return (
              <div key={s.id} className={`exp7-step-item ${active && !done ? 'active' : ''}`}>
                <div className={`exp7-step-number ${done ? 'done' : active ? 'current' : ''}`}>
                  {done ? <Check size={12} /> : s.id}
                </div>
                <div className="exp7-step-content">
                  <div className="exp7-step-title-row">
                    <span className={`exp7-step-title ${active ? 'highlight' : ''}`}>{s.title}</span>
                    <button className="step-info-btn" onClick={(e) => { e.stopPropagation(); setShowInfo(s.id); }}>?</button>
                  </div>
                  {active && !done && <span className="exp7-step-hint">{s.short}</span>}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Lab Bench */}
      <div className="exp7-lab-bench">
        <div className="exp7-bench-header">
          <div className="exp7-bench-title"><FlaskConical size={16} /><h3>Lab Bench</h3></div>
          <span className="exp7-bench-progress">{completedCount}/{steps.length} Complete</span>
        </div>
        <div className="exp7-bench-area">
      <div className="phosphorus-analysis-lab">
        {/* Equipment Row */}
        <div className="equipment-row">
          {/* Analytical Balance */}
          <div className="equipment-section">
            <AnalyticalBalance
              mass={fertilizerMass || 0}
              isTared={balanceTared}
              isStable={true}
              onTare={handleTare}
              onClick={!fertilizerMass ? handleWeighFertilizer : undefined}
            />
            <span className="equipment-label">Analytical Balance</span>
          </div>

          {/* Reaction Beaker with Magnetic Stirrer */}
          <div className="equipment-section">
            <svg viewBox="0 0 120 160" width={120} height={160}>
              <defs>
                <linearGradient id="beaker-glass-p" x1="0%" y1="0%" x2="100%" y2="0%">
                  <stop offset="0%" stopColor="rgba(200,220,240,0.6)" />
                  <stop offset="50%" stopColor="rgba(255,255,255,0.3)" />
                  <stop offset="100%" stopColor="rgba(200,220,240,0.5)" />
                </linearGradient>
              </defs>

              {/* Magnetic stirrer base */}
              <rect x="10" y="130" width="100" height="25" fill="#3A3A3A" rx="5" />
              <circle cx="60" cy="142" r="8" fill="#4A4A4A" />
              {stirring && (
                <circle cx="60" cy="142" r="6" fill="#00A86B">
                  <animate attributeName="opacity" values="1;0.5;1" dur="0.5s" repeatCount="indefinite" />
                </circle>
              )}

              {/* Beaker */}
              <path
                d="M20 20 L20 125 Q20 130 30 130 L90 130 Q100 130 100 125 L100 20"
                fill="url(#beaker-glass-p)"
                stroke="#7A9BB8"
                strokeWidth="2"
              />

              {/* Solution */}
              {dissolved && (
                <rect x="23" y="50" width="74" height="75" fill="#E8F5E9" opacity="0.6" rx="3" />
              )}

              {/* MgNH4PO4 precipitate */}
              {precipitateAmount > 0 && (
                <g>
                  {/* Crystalline precipitate */}
                  <ellipse
                    cx="60"
                    cy="105"
                    rx={precipitateAmount / 100 * 28}
                    ry={precipitateAmount / 100 * 12}
                    fill={getPrecipitateColor()}
                    stroke="#DDDDDD"
                    strokeWidth="0.5"
                  />
                  {crystalline && precipitateAmount > 60 && (
                    <>
                      {/* Crystal facets */}
                      <polygon points="50,100 55,95 60,100 55,105" fill="#FFFFFF" stroke="#EEE" strokeWidth="0.3" />
                      <polygon points="65,102 70,97 75,102 70,107" fill="#FFFFFF" stroke="#EEE" strokeWidth="0.3" />
                      <polygon points="55,108 60,103 65,108 60,113" fill="#FFFFFF" stroke="#EEE" strokeWidth="0.3" />
                    </>
                  )}
                </g>
              )}

              {/* Stir bar */}
              <rect
                x="45"
                y="118"
                width="30"
                height="6"
                fill="#333"
                rx="3"
                style={{
                  transformOrigin: '60px 121px',
                  animation: stirring ? 'rotate 0.5s linear infinite' : 'none'
                }}
              />
            </svg>
            <span className="equipment-label">Reaction Vessel</span>
          </div>

          {/* Vacuum Filtration */}
          <div className="equipment-section">
            <FilterApparatus
              vacuumOn={vacuumOn}
              filterProgress={filterProgress}
              filtrateFillLevel={filterProgress * 0.5}
              filtrateColor="#E8F5E9"
              precipitateAmount={filterProgress > 40 ? filterProgress * 0.8 : 0}
              precipitateColor="#FFFFFF"
              onClick={crystalline && !vacuumOn ? handleStartFiltration : undefined}
            />
          </div>
        </div>

        {/* Status and Progress */}
        <div className="status-section">
          {ammoniaProgress > 0 && ammoniaProgress < 100 && (
            <div className="progress-card">
              <Droplets size={16} />
              <span>Adding NH₃ solution (pH increasing)...</span>
              <div className="progress-bar">
                <div className="progress-fill ammonia" style={{ width: `${ammoniaProgress}%` }} />
              </div>
            </div>
          )}

          {stirring && (
            <div className="progress-card stirring">
              <RotateCw size={16} className="spin" />
              <span>Stirring to form crystalline precipitate...</span>
            </div>
          )}

          {washed && dryingProgress > 0 && dryingProgress < 100 && (
            <div className="progress-card">
              <ThermometerSun size={16} />
              <span>Drying at ~100°C...</span>
              <div className="progress-bar">
                <div className="progress-fill" style={{ width: `${dryingProgress}%` }} />
              </div>
            </div>
          )}
        </div>

        {/* Data Table */}
        <div className="data-table">
          <h4>Gravimetric Data</h4>
          <table>
            <tbody>
              <tr>
                <td>Fertilizer Mass</td>
                <td>{fertilizerMass ? `${fertilizerMass} g` : '-'}</td>
              </tr>
              <tr>
                <td>MgNH₄PO₄·6H₂O (1st)</td>
                <td>{precipitateMass ? `${precipitateMass} g` : '-'}</td>
              </tr>
              <tr>
                <td>MgNH₄PO₄·6H₂O (2nd)</td>
                <td>{secondMass ? `${secondMass} g` : '-'}</td>
              </tr>
              <tr className="result-row">
                <td>% Phosphorus (P)</td>
                <td className="result-value">{percentP ? `${percentP}%` : '-'}</td>
              </tr>
              <tr className="result-row">
                <td>% P₂O₅</td>
                <td className="result-value">{percentP2O5 ? `${percentP2O5}%` : '-'}</td>
              </tr>
            </tbody>
          </table>
        </div>

        {/* Action Buttons */}
        <div className="action-buttons">
          {!fertilizerMass && balanceTared && (
            <div className="action-btn-group">
              <button className="action-btn" onClick={handleWeighFertilizer}>
                <Scale size={16} />
                Weigh Fertilizer (~1.0g)
              </button>
              <button className="step-info-btn" onClick={() => setShowInfo(1)}>?</button>
            </div>
          )}
          {fertilizerMass && !dissolved && (
            <div className="action-btn-group">
              <button className="action-btn" onClick={handleDissolve}>
                <Droplets size={16} />
                Add 25–30 mL H₂O
              </button>
              <button className="step-info-btn" onClick={() => setShowInfo(2)}>?</button>
            </div>
          )}
          {dissolved && !insolubesFiltered && (
            <div className="action-btn-group">
              <button className="action-btn" onClick={handleFilterInsolubles}>
                <Filter size={16} />
                Filter Insolubles
              </button>
              <button className="step-info-btn" onClick={() => setShowInfo(3)}>?</button>
            </div>
          )}
          {insolubesFiltered && !mgso4Added && (
            <div className="action-btn-group">
              <button className="action-btn" onClick={handleAddMgSO4}>
                <Beaker size={16} />
                Add MgSO₄ Solution
              </button>
              <button className="step-info-btn" onClick={() => setShowInfo(4)}>?</button>
            </div>
          )}
          {mgso4Added && !ammoniaAdded && ammoniaProgress === 0 && (
            <div className="action-btn-group">
              <button className="action-btn" onClick={handleAddAmmonia}>
                <Droplets size={16} />
                Add NH₃ (until pH &gt; 9)
              </button>
              <button className="step-info-btn" onClick={() => setShowInfo(5)}>?</button>
            </div>
          )}
          {ammoniaAdded && !crystalline && !stirring && (
            <div className="action-btn-group">
              <button className="action-btn" onClick={handleStir}>
                <RotateCw size={16} />
                Stir to Crystallize
              </button>
              <button className="step-info-btn" onClick={() => setShowInfo(6)}>?</button>
            </div>
          )}
          {filterProgress >= 100 && !washed && (
            <div className="action-btn-group">
              <button className="action-btn" onClick={handleWash}>
                <Droplets size={16} />
                Wash with H₂O then Isopropyl Alcohol
              </button>
              <button className="step-info-btn" onClick={() => setShowInfo(8)}>?</button>
            </div>
          )}
          {washed && dryingProgress === 0 && (
            <div className="action-btn-group">
              <button className="action-btn" onClick={handleStartDrying}>
                <ThermometerSun size={16} />
                Dry at ~100°C
              </button>
              <button className="step-info-btn" onClick={() => setShowInfo(9)}>?</button>
            </div>
          )}
          {dryingComplete && !precipitateMass && (
            <div className="action-btn-group">
              <button className="action-btn" onClick={handleWeighPrecipitate}>
                <Scale size={16} />
                Weigh Precipitate (1st)
              </button>
              <button className="step-info-btn" onClick={() => setShowInfo(9)}>?</button>
            </div>
          )}
          {precipitateMass && !secondMass && (
            <div className="action-btn-group">
              <button className="action-btn" onClick={handleSecondWeigh}>
                <RotateCw size={16} />
                Weigh Precipitate (2nd)
              </button>
              <button className="step-info-btn" onClick={() => setShowInfo(9)}>?</button>
            </div>
          )}
          {secondMass && !percentP && (
            <div className="action-btn-group">
              <button className="action-btn success" onClick={handleCalculate}>
                <Check size={16} />
                Calculate %P and %P₂O₅
              </button>
              <button className="step-info-btn" onClick={() => setShowInfo(10)}>?</button>
            </div>
          )}
        </div>

        {/* Chemical equation */}
        <div className="equation-box">
          <span className="equation">Mg²⁺ + NH₄⁺ + PO₄³⁻ + 6H₂O → MgNH₄PO₄·6H₂O(s)</span>
          <div className="gf-values">
            <span>GF(P) = 30.97/245.40 = 0.1262</span>
            <span>GF(P₂O₅) = 141.92/490.80 = 0.2892</span>
          </div>
        </div>

        <style>{`
          .exp7-part-layout {
            display: flex;
            gap: 16px;
            flex: 1;
            width: 100%;
            min-height: 0;
            padding: 16px;
          }
          .exp7-steps-panel {
            width: 240px;
            background: var(--bg-surface);
            border-radius: 12px;
            border: 1px solid var(--border);
            display: flex;
            flex-direction: column;
            overflow: hidden;
          }
          .exp7-steps-header {
            background: linear-gradient(135deg, var(--primary), var(--primary-dark));
            padding: 14px 16px;
            display: flex;
            align-items: center;
            gap: 10px;
            color: white;
          }
          .exp7-steps-header h3 { font-size: 14px; font-weight: 600; margin: 0; }
          .exp7-steps-list { flex: 1; overflow-y: auto; padding: 12px; }
          .exp7-step-item {
            display: flex;
            align-items: flex-start;
            gap: 12px;
            padding: 8px 10px;
            border-radius: 8px;
            margin-bottom: 2px;
            transition: background 0.15s;
          }
          .exp7-step-item.active { background: rgba(255, 90, 54, 0.15); }
          .exp7-step-number {
            width: 24px; height: 24px; border-radius: 50%;
            display: flex; align-items: center; justify-content: center;
            font-size: 11px; font-weight: 600; flex-shrink: 0;
            background: var(--border); color: var(--text-muted);
          }
          .exp7-step-number.done { background: var(--secondary); color: white; }
          .exp7-step-number.current { background: var(--primary); color: white; }
          .exp7-step-content { flex: 1; min-width: 0; }
          .exp7-step-title-row { display: flex; align-items: center; justify-content: space-between; gap: 8px; }
          .exp7-step-title { font-size: 12px; font-weight: 600; }
          .exp7-step-title.highlight { color: var(--primary-light); }
          .exp7-step-hint { font-size: 11px; color: var(--text-secondary); margin-top: 4px; display: block; }
          .exp7-lab-bench {
            flex: 1;
            background: var(--bg-surface);
            border-radius: 12px;
            border: 1px solid var(--border);
            display: flex;
            flex-direction: column;
            overflow: auto;
          }
          .exp7-bench-header {
            background: linear-gradient(135deg, var(--secondary), var(--secondary-dark));
            padding: 12px 16px;
            display: flex;
            align-items: center;
            justify-content: space-between;
            color: white;
          }
          .exp7-bench-title { display: flex; align-items: center; gap: 10px; }
          .exp7-bench-title h3 { font-size: 14px; font-weight: 600; margin: 0; }
          .exp7-bench-progress { font-size: 12px; opacity: 0.9; }
          .exp7-bench-area {
            flex: 1;
            padding: 20px;
            overflow-y: auto;
          }
          .phosphorus-analysis-lab {
            display: flex;
            flex-direction: column;
            align-items: center;
            gap: 20px;
            padding: 20px;
            width: 100%;
          }

          .equipment-row {
            display: flex;
            gap: 40px;
            flex-wrap: wrap;
            justify-content: center;
            align-items: flex-end;
          }

          .equipment-section {
            display: flex;
            flex-direction: column;
            align-items: center;
            gap: 8px;
          }

          .equipment-label {
            font-size: 11px;
            color: var(--text-secondary);
          }

          @keyframes rotate {
            from { transform: rotate(0deg); }
            to { transform: rotate(360deg); }
          }

          .status-section {
            min-height: 50px;
          }

          .progress-card {
            display: flex;
            align-items: center;
            gap: 12px;
            padding: 12px 20px;
            background: var(--bg-elevated);
            border-radius: 8px;
            font-size: 13px;
            color: var(--text-secondary);
          }

          .progress-card.stirring {
            background: rgba(0, 168, 107, 0.1);
            color: #00A86B;
          }

          .progress-card .spin {
            animation: rotate 1s linear infinite;
          }

          .progress-bar {
            width: 100px;
            height: 6px;
            background: var(--bg-dark);
            border-radius: 3px;
            overflow: hidden;
          }

          .progress-fill {
            height: 100%;
            background: #00A86B;
            transition: width 0.1s;
          }

          .progress-fill.ammonia {
            background: #9C27B0;
          }

          .data-table {
            background: var(--bg-elevated);
            padding: 16px;
            border-radius: 12px;
            width: 100%;
            max-width: 350px;
          }

          .data-table h4 {
            margin: 0 0 12px 0;
            font-size: 13px;
            color: var(--text-secondary);
          }

          .data-table table {
            width: 100%;
            font-size: 12px;
          }

          .data-table td {
            padding: 8px 4px;
            border-bottom: 1px solid var(--border);
          }

          .data-table td:first-child {
            color: var(--text-secondary);
          }

          .data-table td:last-child {
            text-align: right;
            font-family: monospace;
            color: var(--text-primary);
          }

          .result-row td {
            font-weight: 600;
            border-bottom: none;
          }

          .result-value {
            color: #00A86B !important;
            font-size: 14px !important;
          }

          .action-buttons {
            display: flex;
            flex-wrap: wrap;
            gap: 12px;
            justify-content: center;
          }

          .action-btn {
            display: flex;
            align-items: center;
            gap: 8px;
            padding: 12px 20px;
            background: var(--primary);
            color: white;
            border: none;
            border-radius: 8px;
            font-size: 13px;
            font-weight: 500;
            cursor: pointer;
            transition: all 0.2s;
          }

          .action-btn:hover {
            background: var(--primary-dark);
            transform: translateY(-2px);
          }

          .action-btn.success {
            background: #00A86B;
          }

          .action-btn.success:hover {
            background: #008B5B;
          }

          .action-btn-group {
            display: flex;
            align-items: center;
            gap: 8px;
          }

          .equation-box {
            display: flex;
            flex-direction: column;
            gap: 8px;
            padding: 12px 20px;
            background: var(--bg-elevated);
            border-radius: 8px;
            text-align: center;
          }

          .equation {
            font-size: 13px;
            font-family: 'Times New Roman', serif;
            color: var(--text-primary);
          }

          .gf-values {
            display: flex;
            gap: 20px;
            justify-content: center;
          }

          .gf-values span {
            font-size: 10px;
            color: var(--text-muted);
            font-family: monospace;
          }

          .step-info-btn {
            width: 18px;
            height: 18px;
            border-radius: 50%;
            background: var(--accent);
            color: var(--bg-dark);
            font-size: 11px;
            font-weight: 700;
            border: none;
            cursor: pointer;
            display: flex;
            align-items: center;
            justify-content: center;
            flex-shrink: 0;
            transition: transform 0.15s;
          }
          .step-info-btn:hover {
            transform: scale(1.1);
          }
        `}</style>

        {showInfo && stepInfoContent[showInfo] && (
          <InfoBox
            step={showInfo}
            experimentId="exp7"
            stepInfo={stepInfoContent[showInfo]}
            onClose={() => setShowInfo(null)}
          />
        )}
      </div>
      </div>{/* exp7-bench-area */}
      </div>{/* exp7-lab-bench */}
    </div>
  );
}
